import React from 'react'
import { DateFns, YearMonthFns } from '../utils/datetime'

export interface useDateRangePickerResult {
  transitionState: 'none' | 'toNext' | 'toPrev' | 'init'
  onTransitionComplete(): void

  goToNextMonth(): void
  goToPreviousMonth(): void
  goToMonth(month: string): unknown

  yearMonths: string[]
  exitingYearMonths: (string | null)[]

  start: string | null
  end: string | null

  onSelect(date: string): void
  selectionPreview: { start: string; end: string } | null

  setMouseOver(date: string | null): void

  reset(): void
  shouldDisableDate(date: string): boolean
  shouldDisableStartDate(date: string, endDate: string | null): boolean
  shouldDisableEndDate(endDate: string, startDate: string | null): boolean

  currentField: 'start' | 'end' | null
}

export interface useDateRangePickerProps {
  start: string | null
  onStartChange(date: string | null, currentField: 'start' | 'end' | null): unknown

  end: string | null
  onEndChange(date: string | null, currentField: 'start' | 'end' | null): unknown

  currentField: 'start' | 'end' | null

  shouldDisableDate?(date: string): boolean
  shouldDisableStartDate?(startDate: string, endDate: string | null): boolean
  shouldDisableEndDate?(endDate: string, startDate: string | null): boolean
}

export function useDateRangePicker(props: useDateRangePickerProps): useDateRangePickerResult {
  const getInitialState = React.useCallback(() => {
    const firstYearMonth = YearMonthFns.toYearMonth(props.start ? new Date(`${props.start}T00:00`) : new Date())
    return {
      yearMonths: [firstYearMonth, YearMonthFns.getNext(firstYearMonth)],
      exitingYearMonths: [null, null] as (string | null)[],
      transitionState: 'init' as 'none' | 'toNext' | 'toPrev' | 'init',
      mouseOver: null as string | null
    }
  }, [props.start])
  const [{ yearMonths, exitingYearMonths, transitionState, mouseOver }, setState] = React.useState(getInitialState())

  const goToPreviousMonth = React.useCallback(() => {
    setState((s) => {
      const firstYearMonth = s.yearMonths[0]
      const lastYearMonth = s.yearMonths[s.yearMonths.length - 1]
      return {
        exitingYearMonths: [null, lastYearMonth],
        yearMonths: [YearMonthFns.getPrev(firstYearMonth), ...s.yearMonths.slice(0, -1)],
        transitionState: 'toPrev',
        mouseOver: null
      }
    })
  }, [setState])

  const goToNextMonth = React.useCallback(() => {
    setState((s) => {
      const firstYearMonth = s.yearMonths[0]
      const lastYearMonth = s.yearMonths[s.yearMonths.length - 1]
      return {
        exitingYearMonths: [firstYearMonth, null],
        yearMonths: [...s.yearMonths.slice(1), YearMonthFns.getNext(lastYearMonth)],
        transitionState: 'toNext',
        mouseOver: null
      }
    })
  }, [setState])

  const goToMonth = React.useCallback(
    (month: string) => {
      setState((s) => {
        console.log(s.yearMonths, month)
        if (!s.yearMonths.includes(month)) {
          const firstYearMonth = s.yearMonths[0]
          const newYearMonths = Array.from({ length: s.yearMonths.length }).map((_, index) =>
            YearMonthFns.addMonths(month, index)
          )
          if (YearMonthFns.isAfter(month, firstYearMonth)) {
            return {
              exitingYearMonths: s.yearMonths,
              yearMonths: newYearMonths,
              transitionState: 'toNext',
              mouseOver: null
            }
          } else {
            return {
              exitingYearMonths: s.yearMonths,
              yearMonths: newYearMonths,
              transitionState: 'toPrev',
              mouseOver: null
            }
          }
        } else {
          return s
        }
      })
    },
    [setState]
  )

  const onTransitionComplete = React.useCallback(() => {
    setState((s) => ({
      exitingYearMonths: s.yearMonths.map(() => null),
      yearMonths: s.yearMonths,
      transitionState: 'none',
      mouseOver: s.mouseOver
    }))
  }, [setState])

  const previewSelect = React.useCallback(
    (date: string) => {
      if (props.shouldDisableDate && props.shouldDisableDate(date)) {
        return {
          start: props.start,
          end: props.end,
          currentField: props.currentField,
          changed: [] as string[]
        }
      } else if (props.currentField === 'start') {
        if (props.shouldDisableStartDate && props.shouldDisableStartDate(date, props.end)) {
          return {
            start: props.start,
            end: props.end,
            currentField: props.currentField,
            changed: [] as string[]
          }
        } else if (date === props.start) {
          return {
            start: props.start,
            end: props.end,
            currentField: 'end',
            changed: ['start']
          }
        } else if (props.end && DateFns.isEqualOrBefore(date, props.end)) {
          const inRange = DateFns.getDatesInRange(date, props.end)
          const containsDisabled = inRange.some((d) => props.shouldDisableDate && props.shouldDisableDate(d))
          if (containsDisabled) {
            return {
              start: date,
              end: null,
              currentField: 'end',
              changed: ['start', 'end']
            }
          } else {
            return {
              start: date,
              end: props.end,
              currentField: 'end',
              changed: ['start']
            }
          }
        } else if (props.end) {
          return {
            start: date,
            end: null,
            currentField: 'end',
            changed: ['start', 'end']
          }
        } else {
          return {
            start: date,
            end: props.end,
            currentField: 'end',
            changed: ['start']
          }
        }
      } else {
        if (props.shouldDisableEndDate && props.shouldDisableEndDate(date, props.start)) {
          return {
            start: props.start,
            end: props.end,
            currentField: props.currentField,
            changed: [] as string[]
          }
        } else if (props.start && DateFns.isEqualOrAfter(date, props.start)) {
          const inRange = DateFns.getDatesInRange(props.start, date)
          const containsDisabled = inRange.some((d) => props.shouldDisableDate && props.shouldDisableDate(d))
          if (containsDisabled) {
            return {
              start: date,
              end: null,
              currentField: 'end',
              changed: ['start', 'end']
            }
          } else {
            return {
              start: props.start,
              end: date,
              currentField: 'start',
              changed: props.end === date ? [] : ['end']
            }
          }
        } else {
          return {
            start: date,
            end: null,
            currentField: 'end',
            changed: ['start', 'end']
          }
        }
      }
    },
    [props]
  )

  const onSelect = React.useCallback(
    (date: string) => {
      const result = previewSelect(date)
      if (result.changed.includes('start')) {
        props.onStartChange(result.start, result.currentField as 'start' | 'end')
      }
      if (result.changed.includes('end')) {
        props.onEndChange(result.end, result.currentField as 'start' | 'end')
      }
    },
    [previewSelect, props]
  )

  const setMouseOver = React.useCallback((date: string | null) => setState((s) => ({ ...s, mouseOver: date })), [setState])

  const selectionPreview = React.useMemo(() => {
    const resultPreview = mouseOver ? previewSelect(mouseOver) : null
    if (resultPreview?.start && resultPreview.end) {
      return { start: resultPreview.start, end: resultPreview.end }
    } else {
      return null
    }
  }, [mouseOver, previewSelect])

  const reset = React.useCallback(() => setState(getInitialState()), [setState, getInitialState])

  return {
    yearMonths,
    exitingYearMonths,

    transitionState,
    onTransitionComplete,

    goToNextMonth,
    goToPreviousMonth,
    goToMonth,

    start: props.start,
    end: props.end,
    onSelect,
    selectionPreview,

    setMouseOver,
    reset,
    shouldDisableDate: props.shouldDisableDate || alwaysFalse,
    shouldDisableStartDate: props.shouldDisableStartDate || alwaysFalse,
    shouldDisableEndDate: props.shouldDisableEndDate || alwaysFalse,

    currentField: props.currentField
  }
}

const alwaysFalse = () => false
